import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SuperuserRow {
  id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface SuperuserInvitation {
  id: string;
  email: string;
  status: string;
  created_at: string;
  expires_at: string;
  invited_by: string;
}

export function useSuperusers() {
  return useQuery({
    queryKey: ["superusers"],
    staleTime: 60_000,
    queryFn: async (): Promise<SuperuserRow[]> => {
      const { data: admins, error: adminsErr } = await supabase
        .from("admin_users")
        .select("id, role, is_active, created_at");

      if (adminsErr) throw adminsErr;
      const rows = admins ?? [];
      if (rows.length === 0) return [];

      const ids = rows.map((r) => r.id);
      const { data: profiles, error: profilesErr } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", ids);

      if (profilesErr) throw profilesErr;

      const profileMap = new Map(
        (profiles ?? []).map((p) => [p.id, p])
      );

      return rows.map((r) => {
        const profile = profileMap.get(r.id);
        return {
          id: r.id,
          role: String(r.role ?? ""),
          is_active: Boolean(r.is_active),
          created_at: r.created_at ?? "",
          first_name: profile?.first_name ?? "",
          last_name: profile?.last_name ?? "",
          email: profile?.email ?? "",
        };
      });
    },
  });
}

export function useSuperuserInvitations() {
  return useQuery({
    queryKey: ["superuser-invitations"],
    staleTime: 60_000,
    queryFn: async (): Promise<SuperuserInvitation[]> => {
      const { data, error } = await supabase
        .from("superuser_invitations")
        .select("id, email, status, created_at, expires_at, invited_by")
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}
